import { Loader } from "@atoms";
import { useDialog } from "@hooks";
import {
  getAutoCheckInConfirmTitle,
  getAutoCheckInErrorCopy,
  getAutoCheckInToastMessage,
  useAutoCheckIn,
} from "@lib/logic";
import { Canvas } from "@templates";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type AutoCheckInProps = {
  buildingId: string;
};

/**
 * Checks the user in at a scanned building, reached from an app link whose intent is `CheckIn`.
 * See `useAutoCheckIn` for the rules.
 *
 * The page itself only ever shows a loader. Every outcome ends on the home page, where the
 * check-in status has already updated itself: a plain result lands as a toast, while anything
 * that needs more context (a question to answer, a scan that failed) is raised as a dialog.
 */
export const AutoCheckIn = ({ buildingId }: AutoCheckInProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { alertDialog, confirmationDialog } = useDialog();

  const goHome = () => navigate("/", { replace: true });

  useAutoCheckIn(buildingId, {
    onCheckedIn: result => {
      // `icon: null` overrides the checkmark the success toast carries by default; the black
      // toaster styling is what we want, the icon is not.
      toast.success(getAutoCheckInToastMessage(result, t), { icon: null });
      goHome();
    },

    onFailed: error => {
      const { title, message } = getAutoCheckInErrorCopy(error, t);
      alertDialog(title, message, "warning", t("workplace:AutoCheckInGoToApp")).then(goHome);
    },

    askToConvert: ({ buildingName, plannedStatus }) =>
      confirmationDialog(
        getAutoCheckInConfirmTitle(plannedStatus, t),
        t("workplace:AutoCheckInConfirmMessage", { buildingName }),
        "office",
        t("workplace:AutoCheckInConfirmAccept"),
        t("workplace:AutoCheckInConfirmDecline"),
      ),
  });

  return (
    <Canvas centerContent>
      <div className="flex flex-auto items-center justify-center p-8">
        <Loader />
      </div>
    </Canvas>
  );
};
